import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import { Dialog, DialogButton, DialogContentText } from '@/components/Dialog';
import useDataHubExport from '@/features/DataHub/modules/Export/hooks/useDataHubExport';
import useUserDetails from '@/hooks/useUserDetails';

import type { ExportContextType } from '../types/context.types';

const ExportContext = createContext<ExportContextType | undefined>(undefined);

export const useExport = () => {
  const context = useContext(ExportContext);
  if (!context) throw new Error('useExportContext must be used within ExportProvider.');
  return context;
};

const ExcelExportModalsWrapper = () => {
  const { excelExportStatus, resetExcelExportStatus } = useExport();

  const isLoading = excelExportStatus === 'loading';
  const isQueued = excelExportStatus === 'queued';

  const { userEmail } = useUserDetails();

  return (
    <>
      <Dialog
        open={isLoading}
        onClose={() => {}}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', height: '100%', py: '16px' }}>
          <CircularProgress size={24} />
          <DialogContentText>Your request is being processed...</DialogContentText>
        </Box>
      </Dialog>

      <Dialog
        buttons={<DialogButton onClick={resetExcelExportStatus}>OK</DialogButton>}
        open={isQueued}
        title="We're on it."
        onClose={resetExcelExportStatus}
      >
        <DialogContentText>
          You will get an email shortly to{' '}
          <Typography
            sx={{
              textDecoration: 'underline',
              display: 'inline'
            }}
          >
            {userEmail}
          </Typography>
        </DialogContentText>
      </Dialog>
    </>
  );
};

export const ExportProvider = ({ children }: { children: ReactNode }) => {
  const exportContext = useDataHubExport();
  return (
    <ExportContext.Provider value={exportContext}>
      {children}
      <ExcelExportModalsWrapper />
    </ExportContext.Provider>
  );
};
